import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getShipmentPalletLabels } from "@/lib/bol.functions";
import { Button } from "@/components/ui/button";
import { Barcode } from "@/components/barcode";
import { Printer, ArrowLeft } from "lucide-react";

export const Route = createFileRoute(
  "/_authenticated/app/shipments/$shipmentId/pallet-labels",
)({ component: PalletLabelsPage });

function addressLine(a: any): string {
  if (!a) return "";
  if (typeof a === "string") return a;
  const parts = [a.street1, a.street2, [a.city, a.state, a.postal_code].filter(Boolean).join(", "), a.country]
    .filter(Boolean);
  return parts.join("\n");
}

function PalletLabelsPage() {
  const { shipmentId } = Route.useParams();
  const fetchFn = useServerFn(getShipmentPalletLabels);
  const { data, isLoading, error } = useQuery({
    queryKey: ["pallet-labels", shipmentId],
    queryFn: () => fetchFn({ data: { shipmentId } }),
  });

  useEffect(() => {
    document.title = "Pallet labels";
  }, []);

  if (isLoading) return <div className="p-10 text-sm text-muted-foreground">Loading…</div>;
  if (error) return <div className="p-10 text-sm text-destructive">{(error as Error).message}</div>;
  if (!data) return null;

  const labels = data.labels ?? [];

  return (
    <div className="min-h-screen bg-muted/40">
      <div className="print:hidden border-b bg-card">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button variant="ghost" asChild>
            <Link to="/app/shipments/$shipmentId" params={{ shipmentId }}>
              <ArrowLeft className="h-4 w-4 mr-2" />Back to shipment
            </Link>
          </Button>
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground">
              {labels.length} pallet label{labels.length === 1 ? "" : "s"}
            </span>
            <Button onClick={() => window.print()} disabled={labels.length === 0}>
              <Printer className="h-4 w-4 mr-2" />Print labels
            </Button>
          </div>
        </div>
      </div>

      {labels.length === 0 ? (
        <div className="max-w-3xl mx-auto p-10 text-sm text-muted-foreground">
          No pallets (LPNs) are assigned to this shipment yet.
        </div>
      ) : (
        <div className="max-w-5xl mx-auto p-6 print:p-0 space-y-4 print:space-y-0">
          {labels.map((l: any) => (
            <PalletLabel key={l.id} label={l} shipper={data.shipper} consignee={data.consignee} shipment={data.shipment} />
          ))}
        </div>
      )}

      <style>{`
        @media print {
          @page { size: 4in 6in; margin: 0; }
          body { background: white; }
          .pallet-label { page-break-after: always; box-shadow: none !important; }
          .pallet-label:last-child { page-break-after: auto; }
        }
      `}</style>
    </div>
  );
}

function PalletLabel({
  label, shipper, consignee, shipment,
}: { label: any; shipper: any; consignee: any; shipment: any }) {
  return (
    <div
      className="pallet-label bg-white text-black mx-auto shadow-sm print:shadow-none border border-black print:border-0 flex flex-col"
      style={{ width: "4in", height: "6in" }}
    >
      {/* Header: From / To */}
      <div className="grid grid-cols-2 border-b border-black text-[9px]">
        <div className="p-2 border-r border-black">
          <div className="uppercase tracking-wider text-neutral-600">From</div>
          <div className="font-semibold text-[11px] leading-tight">{shipper.name || "—"}</div>
          <div className="whitespace-pre-line leading-tight">{addressLine(shipper.address)}</div>
        </div>
        <div className="p-2">
          <div className="uppercase tracking-wider text-neutral-600">Ship To</div>
          <div className="font-semibold text-[11px] leading-tight">{consignee.name || "—"}</div>
          <div className="whitespace-pre-line leading-tight">{addressLine(consignee.address)}</div>
        </div>
      </div>

      {/* Refs row */}
      <div className="grid grid-cols-3 border-b border-black text-[9px]">
        <RefCell label="PO #" value={consignee.po_number} />
        <RefCell label="Order #" value={consignee.order_number} />
        <RefCell label="Carrier / PRO" value={[shipment.carrier, shipment.pro_number].filter(Boolean).join(" / ")} />
      </div>

      {/* Pallet type + count */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-black">
        <div>
          <div className="text-[9px] uppercase tracking-wider text-neutral-600">Pallet</div>
          <div className="text-xl font-bold leading-none">
            {label.seq} <span className="text-base font-normal">of {label.of}</span>
          </div>
        </div>
        <div className="text-right">
          <div className="text-[9px] uppercase tracking-wider text-neutral-600">Type / Weight</div>
          <div className="text-sm font-semibold leading-none">
            {(label.type ?? "Pallet").toUpperCase()} · {label.weight_lb ?? 0} lb
          </div>
        </div>
      </div>

      {/* SSCC barcode (big) */}
      <div className="flex-1 flex flex-col items-center justify-center px-2 py-3">
        <div className="text-[9px] uppercase tracking-wider text-neutral-600 mb-1">SSCC (00)</div>
        {label.sscc ? (
          <>
            <Barcode value={label.sscc} format="CODE128" height={110} width={2.2} displayValue={false} margin={0} />
            <div className="font-mono text-base tracking-wider mt-2">
              ({"00"}) {formatSscc(label.sscc)}
            </div>
          </>
        ) : (
          <div className="text-xs text-neutral-600">No SSCC assigned</div>
        )}
      </div>

      {/* Footer */}
      <div className="border-t border-black px-3 py-1 flex items-center justify-between text-[9px] text-neutral-700">
        <span className="font-mono">{shipment.id.slice(0, 8)}</span>
        <span className="uppercase">{shipment.freight_mode ?? "—"}</span>
      </div>
    </div>
  );
}

function RefCell({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="p-2 border-r last:border-r-0 border-black">
      <div className="uppercase tracking-wider text-neutral-600">{label}</div>
      <div className="font-semibold text-[11px] leading-tight">{value || "—"}</div>
    </div>
  );
}

function formatSscc(s: string): string {
  // Group in 4s for readability
  return s.replace(/(.{4})/g, "$1 ").trim();
}